// src/utils/ai.js
import axios from "axios";

axios.defaults.timeout = 25000;

let OPENAI_KEY = "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";


/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

function ensureKey() {
  if (!OPENAI_KEY) {
    throw new Error("OpenAI API key missing. Call AI.setKey('YOUR_KEY') first.");
  }
}

function unwrapOpenAIError(err) {
  const msg =
    err?.response?.data?.error?.message ||
    err?.response?.data?.message ||
    err?.message ||
    String(err);
  return new Error(`OpenAI error: ${msg}`);
}

/* ------------------------------------------------------------------ */
/* Keywords (from Code 5)                                              */
/* ------------------------------------------------------------------ */

const STOP_WORDS = new Set([
  "the","and","with","that","this","from","your","into","over","under","for","you",
  "are","was","were","will","can","could","should","would","have","has","had","about",
  "onto","than","then","also","such","like","when","what","how","why","where","which",
  "who","while","to","in","on","of","a","an","is","it","at","as","by","or","if","be",
  "we","they","them","their","our","us","he","she","him","her","his","hers","its",
  "up","down","off","out","so","no","not","just","only","very"
]);

export function basicKeywords(text = "", optionsOrLimit) {
  let limit = 10;
  let minLength = 3;

  if (typeof optionsOrLimit === "number") {
    limit = optionsOrLimit;
  } else if (optionsOrLimit && typeof optionsOrLimit === "object") {
    if (typeof optionsOrLimit.limit === "number") limit = optionsOrLimit.limit;
    if (typeof optionsOrLimit.minLength === "number") minLength = optionsOrLimit.minLength;
  }

  if (!text || typeof text !== "string") return [];

  const matches = text.toLowerCase().match(/[a-z][a-z-]+/g);
  if (!matches) return [];

  const freq = Object.create(null);
  for (let raw of matches) {
    const w = raw.replace(/^-+|-+$/g, "");
    if (!w || w.length < minLength) continue;
    if (STOP_WORDS.has(w)) continue;
    freq[w] = (freq[w] || 0) + 1;
  }

  return Object.keys(freq)
    .sort((a, b) => freq[b] - freq[a])
    .slice(0, limit);
}

/* ------------------------------------------------------------------ */
/* Optional: parse "prompt | size:... | style:... | n:2 | quality:high" */
/* ------------------------------------------------------------------ */

export function parseImageInput(input) {
  const parts = String(input || "")
    .split("|")
    .map(s => s.trim())
    .filter(Boolean);

  const out = {
    prompt: "",
    size: "1024x1024",
    n: 1,
    style: "",
    quality: /** @type {"low"|"medium"|"high"} */ ("medium"),
    model: undefined,
    seed: undefined
  };

  if (!parts.length) return out;

  if (!/^[a-z0-9_-]+\s*:/i.test(parts[0])) out.prompt = parts[0];

  parts.forEach(seg => {
    const m = seg.match(/^([a-z]+)\s*:\s*(.+)$/i);
    if (!m) return;
    const key = m[1].toLowerCase();
    const val = m[2].trim();

    if (key === "size") out.size = val;
    else if (key === "n") {
      const n = parseInt(val, 10);
      out.n = Number.isFinite(n) ? Math.max(1, Math.min(4, n)) : 1;
    } else if (key === "style") out.style = val;
    else if (key === "quality") {
      if (/^hd$/i.test(val)) out.quality = "high";
      else if (/^standard$/i.test(val)) out.quality = "medium";
      else if (/^(low|medium|high)$/i.test(val)) out.quality = val.toLowerCase();
    } else if (key === "model") out.model = val;
    else if (key === "seed") {
      const seed = parseInt(val, 10);
      if (Number.isFinite(seed)) out.seed = seed;
    }
  });

  return out;
}

/* ------------------------------------------------------------------ */
/* AI API                                                              */
/* ------------------------------------------------------------------ */

/**
 * @typedef {Object} ChatOptions
 * @property {string} [model]
 * @property {number} [temperature]
 * @property {number} [max_tokens]
 * @property {Object} [extra]       // pass-through extras
 */

export const AI = {
  setKey(k) { OPENAI_KEY = k; },

  // match Code 5 default
  model: "gpt-5-chat-latest",
  setModel(m) { this.model = m; },

  /**
   * Low-level wrapper: messages[] OR (system, user)
   * Same endpoint as Code 5: https://api.openai.com/v1/chat/completions
   */
  async chatComplete(systemOrMessages, userPrompt = null, modelOrOpts = null) {
    ensureKey();

    let useModel = this.model || "gpt-5-chat-latest";
    let temperature = 0.2;
    let extraParams = {};

    if (typeof modelOrOpts === "string" && modelOrOpts) {
      useModel = modelOrOpts;
    } else if (modelOrOpts && typeof modelOrOpts === "object") {
      if (typeof modelOrOpts.model === "string") useModel = modelOrOpts.model;
      if (typeof modelOrOpts.temperature === "number") temperature = modelOrOpts.temperature;

      extraParams = { ...modelOrOpts };
      delete extraParams.model;
      delete extraParams.temperature;
    }

    const messages = Array.isArray(systemOrMessages)
      ? systemOrMessages.map(m => ({
          role: m.role,
          content: String(m.content || "")
        }))
      : [
          { role: "system", content: String(systemOrMessages || "") },
          { role: "user", content: String(userPrompt || "") }
        ];

    try {
      const res = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        {
          model: useModel,
          messages,
          temperature,
          ...extraParams
        },
        {
          headers: {
            Authorization: `Bearer ${OPENAI_KEY}`,
            "Content-Type": "application/json"
          }
        }
      );

      return String(res.data?.choices?.[0]?.message?.content || "").trim();
    } catch (err) {
      throw unwrapOpenAIError(err);
    }
  },

  /**
   * ✅ What your EventPlanner/EventFab code expects:
   * AI.chatWithSystem(system, user, opts?) -> { text, keywords }
   */
  async chatWithSystem(system, user, optsOrModel = null) {
    let opts = {};

    if (typeof optsOrModel === "string") {
      opts = { model: optsOrModel };
    } else if (optsOrModel && typeof optsOrModel === "object") {
      opts = { ...optsOrModel };
    }

    if (typeof opts.temperature !== "number") opts.temperature = 0.3;

    const text = await this.chatComplete(String(system || ""), String(user || ""), opts);
    return { text, keywords: basicKeywords(text) };
  },

  /**
   * Optional convenience, same shape as your Code 5
   */
  async chat(userPrompt, optsOrModel = null) {
    const sys =
      "You are COGNEETIV (Enhanced AI). Be concise, helpful, and structured when appropriate. Use markdown headings and bullets when it improves readability.";

    let opts = {};
    if (typeof optsOrModel === "string") opts = { model: optsOrModel };
    else if (optsOrModel && typeof optsOrModel === "object") opts = { ...optsOrModel };

    if (typeof opts.temperature !== "number") opts.temperature = 0.3;

    const text = await this.chatComplete(sys, String(userPrompt || ""), opts);
    return { text, keywords: basicKeywords(text) };
  },

  /**
   * Low-level images call, same endpoint as Code 5:
   * https://api.openai.com/v1/images/generations
   */
  async generateImage(params = {}) {
    ensureKey();

    const {
      prompt,
      size = "1024x1024",
      n = 1,
      quality = "standard", // can be "low|medium|high" too; we normalize
      style = "",
      model = "dall-e-3"
    } = params || {};

    if (!prompt) throw new Error("Image prompt is required.");

    const fullPrompt = style ? `${prompt}\n\nStyle: ${style}` : prompt;

    // normalize quality -> "standard" | "hd" for dall-e-3
    let qualityNorm = quality;
    if (/^low$/i.test(quality)) qualityNorm = "standard";
    else if (/^medium$/i.test(quality)) qualityNorm = "standard";
    else if (/^high$/i.test(quality)) qualityNorm = "hd";
    else if (!/^(standard|hd)$/i.test(String(qualityNorm))) qualityNorm = "standard";

    try {
      const res = await axios.post(
        "https://api.openai.com/v1/images/generations",
        {
          model,
          prompt: fullPrompt,
          n: Math.max(1, Math.min(4, Number(n || 1))),
          size,
          quality: qualityNorm,
          response_format: "url"
        },
        {
          headers: {
            Authorization: `Bearer ${OPENAI_KEY}`,
            "Content-Type": "application/json"
          },
          timeout: 120000
        }
      );

      const urls = (res.data?.data || [])
        .map(x => x?.url)
        .filter(Boolean);

      if (!urls.length) throw new Error("Image generation returned no URLs.");
      return { urls, raw: res.data };
    } catch (err) {
      throw unwrapOpenAIError(err);
    }
  },

  /**
   * ✅ Matches your EventPlanner usage:
   * const result = await AI.image(prompt, { size, style })
   * Returns { text, imageUrls, keywords }
   */
  async image(userPrompt, opts = {}) {
    // support both raw prompt and pipe format
    const parsed = (typeof userPrompt === "string" && userPrompt.includes("|"))
      ? parseImageInput(userPrompt)
      : { prompt: String(userPrompt || "") };

    const size = opts.size || parsed.size || "1024x1024";
    const style = opts.style || parsed.style || "";
    const n = opts.n || parsed.n || 1;
    const quality = opts.quality || parsed.quality || "medium";
    const model = opts.model || parsed.model || "dall-e-3";

    const { urls } = await this.generateImage({
      prompt: parsed.prompt,
      size,
      n,
      quality,
      style,
      model
    });

    const text = urls.length === 1
      ? `✅ Image generated:\n${urls[0]}`
      : `✅ ${urls.length} images generated:\n${urls.join("\n")}`;

    return {
      text,
      imageUrls: urls,
      keywords: basicKeywords(parsed.prompt || "")
    };
  }
};

export default AI;
